import { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Login from "./Login";
import UsersPage from "./UsersPage";
import BuffHistoryPage from "./BuffHistoryPage";
import AdminPage from "./AdminPage";
import GroupsPage from "./GroupsPage";
import Sidebar from "./Sidebar";
import {
  PAGE_SIZE, buffCount, fa, fdateParts,
  userHasGroupAccess
} from "./data";
import { api, ApiError, getSession, setSession, clearSession, loadAppState, mapApiUser } from "./api";
import { DatePickerField } from "./DatePickerField";
import "./App.css";

const EMPTY_FILTERS = { groupId: "all", personQuery: "" };

function tallyMarks(count) {
  const groups = Math.floor(count / 5);
  const remainder = count % 5;
  const marks = [];
  for (let i = 0; i < groups; i++) {
    marks.push(
      <span className="grp" key={"g" + i} aria-label="دسته پنج‌تایی">
        <i></i><i></i><i></i><i></i><em></em>
      </span>
    );
  }
  if (remainder) {
    const sticks = [];
    for (let j = 0; j < remainder; j++) sticks.push(<span className="stick" key={j}></span>);
    marks.push(<span className="cur" key="cur" aria-label={remainder + " بازخورد"}>{sticks}</span>);
  }
  if (!marks.length) return <span style={{ fontSize: 10, color: "#929889" }}>هنوز بازخوردی ثبت نشده</span>;
  return marks;
}

function computeOrdinals(list) {
  const asc = [...list].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
  const counters = { negative: 0, positive: 0 };
  const map = {};
  asc.forEach(item => {
    counters[item.type] += 1;
    map[item.id] = counters[item.type];
  });
  return { map, totals: counters };
}

function buffStatus(ordinal, threshold) {
  const t = Number(threshold) > 0 ? Number(threshold) : 5;
  const position = ((ordinal - 1) % t) + 1;
  const buff = buffCount(ordinal, t);
  if (position === t) {
    return { label: "این بازخورد باف شماره " + fa(buff) + " را کامل کرده است", threshold: true };
  }
  return { label: "مرحله " + fa(position) + " از " + fa(t) + " تا باف بعدی", threshold: false };
}

function FilterBar({ groups, filters, onChange, onReset }) {
  const active = filters.groupId !== "all" || filters.personQuery;
  return (
    <section className="toolbar filters-bar" aria-label="فیلترها">
      <div className="filters-grid">
        <div className="filter-field">
          <label htmlFor="filterGroup">فیلتر گروه</label>
          <select id="filterGroup" className="input select" value={filters.groupId} onChange={(e) => onChange({ ...filters, groupId: e.target.value })}>
            <option value="all">همه گروه‌ها</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label htmlFor="filterPerson">فیلتر شخص</label>
          <input id="filterPerson" className="input" placeholder="نام کارشناس…" value={filters.personQuery} onChange={(e) => onChange({ ...filters, personQuery: e.target.value })} />
        </div>
      </div>
      <div className="filters-actions">
        <button className="btn" type="button" disabled={!active} onClick={onReset}>حذف فیلترها</button>
      </div>
    </section>
  );
}

function PersonCard({ person, feedbacks, settings, canAddFeedback, canUpdate, canDelete, canApplyBuff, onAddFeedback, onOpenHistory, onRename, onDelete, onApplyBuff }) {
  const activeFeedbacks = feedbacks.filter(f => !f.applied_buff_id);
  const negList = activeFeedbacks.filter(f => f.type === "negative");
  const posList = activeFeedbacks.filter(f => f.type === "positive");
  const negBuff = buffCount(negList.length, settings.negativeThreshold);
  const posBuff = buffCount(posList.length, settings.positiveThreshold);
  const hasCompletedBuff = negBuff > 0 || posBuff > 0;

  return (
    <article className="person in-group">
      <div className="person-top">
        <div className="name-block">
          <div className="name">{person.name}</div>
          <div className="meta">هر {fa(settings.negativeThreshold)} بازخورد منفی = ۱ باف منفی · هر {fa(settings.positiveThreshold)} بازخورد مثبت = ۱ باف مثبت</div>
        </div>
        {canAddFeedback && (
          <button className="plus" type="button" title={"ثبت بازخورد برای " + person.name} onClick={() => onAddFeedback(person.id)}>+</button>
        )}
      </div>

      <div className="tally-row">
        <span className="tally-tag tally-tag-negative">منفی</span>
        <div className="tallies tallies-negative" title={fa(negList.length) + " بازخورد منفی"}>{tallyMarks(negList.length)}</div>
        <div className="count"><b>{fa(negList.length)}</b><span>بازخورد</span></div>
        <div className="pen"><b>−{fa(negBuff)}</b><span>باف منفی</span></div>
      </div>

      <div className="tally-row">
        <span className="tally-tag tally-tag-positive">مثبت</span>
        <div className="tallies tallies-positive" title={fa(posList.length) + " بازخورد مثبت"}>{tallyMarks(posList.length)}</div>
        <div className="count"><b>{fa(posList.length)}</b><span>بازخورد</span></div>
        <div className="pos"><b>+{fa(posBuff)}</b><span>باف مثبت</span></div>
      </div>

      <div className="actions">
        <button className="small" type="button" onClick={() => onOpenHistory(person.id)}>شرح بازخوردها ({fa(feedbacks.length)})</button>
        {canUpdate && <button className="small" type="button" onClick={() => onRename(person.id)}>تغییر نام</button>}
        {canApplyBuff && (
          <button className="small" type="button" disabled={!hasCompletedBuff} title={hasCompletedBuff ? "اعمال باف‌های کامل‌شدهٔ این فرد" : "هنوز بافی کامل نشده است"} onClick={() => onApplyBuff(person.id)}>
            اعمال باف{hasCompletedBuff ? " (" + fa(negBuff + posBuff) + ")" : ""}
          </button>
        )}
        {canDelete && <button className="small danger" type="button" onClick={() => onDelete(person.id)}>حذف فرد</button>}
      </div>
    </article>
  );
}

function GroupCard({ group, members, feedbacksByPerson, settings, canAddFeedback, canAddPerson, canUpdate, canDelete, canApplyBuff, canManageUsers, isOpen, onToggle, onAddPerson, onRenameGroup, onDeleteGroup, onManageAccess, ...personHandlers }) {
  const groupFeedbacks = members.reduce((sum, p) => sum + feedbacksByPerson(p.id).length, 0);
  const groupNegBuff = members.reduce((sum, p) => sum + buffCount(feedbacksByPerson(p.id).filter(f => f.type === "negative" && !f.applied_buff_id).length, settings.negativeThreshold), 0);
  const groupPosBuff = members.reduce((sum, p) => sum + buffCount(feedbacksByPerson(p.id).filter(f => f.type === "positive" && !f.applied_buff_id).length, settings.positiveThreshold), 0);

  return (
    <section className={"group-card" + (isOpen ? " is-open" : "")}>
      <div className="group-header">
        <button className="group-toggle" type="button" aria-expanded={isOpen} onClick={() => onToggle(group.id)}>
          <span className="group-icon">{(group.name || "گ").trim().charAt(0) || "گ"}</span>
          <span className="group-title-wrap">
            <span className="group-title-line"><span className="group-title">{group.name}</span></span>
            <span className="group-meta">
              <span>{fa(members.length)} کارشناس</span><span>•</span>
              <span>{fa(groupFeedbacks)} بازخورد</span><span>•</span>
              <span>−{fa(groupNegBuff)} / +{fa(groupPosBuff)} باف</span>
            </span>
          </span>
          <span className="group-chevron" aria-hidden="true">⌄</span>
        </button>
        <div className="group-head-actions">
          {canAddPerson && (
            <button className="btn btn-soft group-add-person" type="button" onClick={() => onAddPerson(group.id)}>＋ افزودن کارشناس</button>
          )}
          {canManageUsers && (
            <button className="btn group-menu-btn" type="button" title="دسترسی کاربران" onClick={() => onManageAccess(group.id)}>👤</button>
          )}
          {!group.system && canUpdate && (
            <button className="btn group-menu-btn" type="button" title="تغییر نام گروه" onClick={() => onRenameGroup(group.id)}>✎</button>
          )}
          {!group.system && canDelete && (
            <button className="btn btn-danger group-menu-btn" type="button" title="حذف گروه" onClick={() => onDeleteGroup(group.id)}>×</button>
          )}
        </div>
      </div>
      <div className="group-body">
        <div className="group-people">
          {members.length ? (
            members.map(p => (
              <PersonCard
                key={p.id}
                person={p}
                feedbacks={feedbacksByPerson(p.id)}
                settings={settings}
                canAddFeedback={canAddFeedback}
                canUpdate={canUpdate}
                canDelete={canDelete}
                canApplyBuff={canApplyBuff}
                {...personHandlers}
              />
            ))
          ) : (
            <div className="group-empty"><strong>هنوز کارشناسی در این گروه نیست.</strong>{canAddPerson ? "از دکمه «افزودن کارشناس» استفاده کنید." : ""}</div>
          )}
        </div>
        {canAddPerson && (
          <button className="btn group-person-add-inline" type="button" onClick={() => onAddPerson(group.id)}>
            ＋ افزودن کارشناس جدید به «{group.name}»
          </button>
        )}
      </div>
    </section>
  );
}

function FeedbackModal({ person, onCancel, onSave }) {
  const [type, setType] = useState("negative");
  const [caption, setCaption] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setType("negative");
    setCaption("");
    setImageFile(null);
    setPreview(null);
  }, [person?.id]);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  if (!person) return null;

  function handleFile(e) {
    const file = e.target.files[0] || null;
    setImageFile(file);
    setPreview(file ? URL.createObjectURL(file) : null);
  }

  async function handleSave() {
    setBusy(true);
    try {
      await onSave({ type, caption, imageFile });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-bg" style={{ display: "block" }} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <section className="modal" role="dialog" aria-modal="true">
        <h3>ثبت بازخورد</h3>
        <div className="who">برای: {person.name}</div>

        <div className="type-toggle">
          <button type="button" className={"type-btn negative" + (type === "negative" ? " active" : "")} onClick={() => setType("negative")}>بازخورد منفی</button>
          <button type="button" className={"type-btn positive" + (type === "positive" ? " active" : "")} onClick={() => setType("positive")}>بازخورد مثبت</button>
        </div>

        <label className="sr-only" htmlFor="caption">توضیح بازخورد</label>
        <textarea id="caption" className="textarea" placeholder="کپشن / توضیح بازخورد (اختیاری)…" value={caption} onChange={(e) => setCaption(e.target.value)} autoFocus />

        <div className="image-field">
          <label className="btn btn-soft image-pick">
            📎 افزودن تصویر
            <input className="sr-only" type="file" accept="image/*" onChange={handleFile} />
          </label>
          {preview && (
            <div className="image-preview-wrap">
              <img className="image-preview" src={preview} alt="پیش‌نمایش" />
              <button type="button" className="small danger" onClick={() => { setImageFile(null); setPreview(null); }}>حذف تصویر</button>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn" type="button" onClick={onCancel} disabled={busy}>انصراف</button>
          <button className="btn btn-primary" type="button" onClick={handleSave} disabled={busy}>{busy ? "در حال ذخیره…" : "ثبت بازخورد"}</button>
        </div>
      </section>
    </div>
  );
}

function EntityModal({ mode, onCancel, onSave }) {
  const [name, setName] = useState("");

  useEffect(() => { setName(""); }, [mode]);

  if (!mode) return null;
  const title = mode === "group" ? "افزودن گروه جدید" : "افزودن کارشناس";
  const note = mode === "group"
    ? "نام گروه را وارد کنید. گروه جدید به انتهای فهرست اضافه می‌شود."
    : "نام کارشناس را وارد کنید؛ سوابق بعدی او داخل همین گروه نگهداری می‌شود.";
  const placeholder = mode === "group" ? "مثلاً روابط عمومی" : "نام و نام خانوادگی کارشناس";
  return (
    <div className="modal-bg" style={{ display: "block" }} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <section className="modal" role="dialog" aria-modal="true">
        <h3>{title}</h3>
        <p className="entity-modal-note">{note}</p>
        <label className="sr-only" htmlFor="entityNameInput">نام</label>
        <input
          id="entityNameInput"
          className="input"
          autoComplete="off"
          placeholder={placeholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onSave(name); }}
          autoFocus
        />
        <div className="modal-actions">
          <button className="btn" type="button" onClick={onCancel}>انصراف</button>
          <button className="btn btn-primary" type="button" onClick={() => onSave(name)}>ذخیره</button>
        </div>
      </section>
    </div>
  );
}

function HistoryModal({ person, feedbacks, settings, canDelete, page, onPageChange, onClose, onDeleteFeedback }) {
  if (!person) return null;

  const total = feedbacks.length;
  const { map: ordinalMap, totals } = computeOrdinals(feedbacks);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), pageCount);
  const start = (currentPage - 1) * PAGE_SIZE;
  const pageItems = feedbacks.slice(start, start + PAGE_SIZE);
  const negBuff = buffCount(totals.negative, settings.negativeThreshold);
  const posBuff = buffCount(totals.positive, settings.positiveThreshold);

  return (
    <div className="history-modal-bg" style={{ display: "block" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="history-modal" role="dialog" aria-modal="true">
        <div className="history-modal-head">
          <div>
            <h3>شرح بازخوردها</h3>
            <div className="who">کارشناس: {person.name}</div>
          </div>
          <button className="modal-close" type="button" aria-label="بستن" onClick={onClose}>×</button>
        </div>
        <div className="history-summary">
          {fa(totals.negative)} بازخورد منفی (−{fa(negBuff)} باف) · {fa(totals.positive)} بازخورد مثبت (+{fa(posBuff)} باف)
        </div>
        <div className="history-list">
          {!total ? (
            <div className="history-empty">هنوز بازخوردی برای این کارشناس ثبت نشده است.</div>
          ) : (
            pageItems.map((f) => {
              const ordinal = ordinalMap[f.id];
              const threshold = f.type === "positive" ? settings.positiveThreshold : settings.negativeThreshold;
              const dateParts = fdateParts(f.created_at);
              const status = buffStatus(ordinal, threshold);
              const isPositive = f.type === "positive";
              return (
                <article className="history-item" key={f.id}>
                  <div className="history-item-head">
                    <div className="history-item-number">
                      <span className={"feedback-type-badge" + (isPositive ? " positive" : " negative")}>{isPositive ? "مثبت" : "منفی"}</span>
                      {" "}بازخورد {fa(ordinal)}
                      {f.applied_buff_id && <span className="buff-status applied-badge">باف این مورد اعمال شده</span>}
                    </div>
                    <div className="history-item-date">{dateParts.date}</div>
                  </div>
                  <div className="history-item-meta">
                    <div className="history-time">ساعت {dateParts.time || "—"}</div>
                    <span className={"buff-status" + (status.threshold ? " threshold" : "")}>{status.label}</span>
                  </div>
                  {f.caption && <div className="history-item-text">{f.caption}</div>}
                  {f.image && <img className="history-item-image" src={f.image} alt="تصویر بازخورد" />}
                  {canDelete && (
                    <div className="history-item-actions">
                      <button className="small danger" type="button" onClick={() => onDeleteFeedback(f.id, person)}>حذف این بازخورد</button>
                    </div>
                  )}
                </article>
              );
            })
          )}
        </div>
        {pageCount > 1 && (
          <div className="pagination">
            <button className="btn btn-icon" type="button" disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>‹</button>
            <span className="pagination-info">صفحه {fa(currentPage)} از {fa(pageCount)}</span>
            <button className="btn btn-icon" type="button" disabled={currentPage >= pageCount} onClick={() => onPageChange(currentPage + 1)}>›</button>
          </div>
        )}
      </section>
    </div>
  );
}

function ApplyBuffModal({ person, pending, onCancel, onConfirm }) {
  const [note, setNote] = useState("");
  const [includeNegative, setIncludeNegative] = useState(true);
  const [includePositive, setIncludePositive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNote("");
    setIncludeNegative(pending.negative > 0);
    setIncludePositive(pending.positive > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.id]);

  if (!person) return null;
  const selectedTotal = (includeNegative ? pending.negative : 0) + (includePositive ? pending.positive : 0);

  async function handleConfirm() {
    if (!selectedTotal) return;
    setBusy(true);
    try {
      await onConfirm(note, { negative: includeNegative, positive: includePositive });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-bg" style={{ display: "block" }} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <section className="modal" role="dialog" aria-modal="true">
        <h3>اعمال باف</h3>
        <div className="who">برای: {person.name}</div>
        <p className="entity-modal-note">
          مشخص کنید کدام نوع باف اعمال شود. بافی که روشن نباشد، اعمال نمی‌شود و بازخوردهایش دست‌نخورده می‌ماند و ماه به ماه ادامه پیدا می‌کند.
        </p>

        <div className="switch-row">
          <label className="switch">
            <input type="checkbox" checked={includeNegative} disabled={!pending.negative} onChange={(e) => setIncludeNegative(e.target.checked)} />
            <span className="switch-track"><span className="switch-thumb"></span></span>
          </label>
          <span>باف منفی ({fa(pending.negative)} عدد){!pending.negative ? " — بافی کامل نشده" : ""}</span>
        </div>
        <div className="switch-row">
          <label className="switch">
            <input type="checkbox" checked={includePositive} disabled={!pending.positive} onChange={(e) => setIncludePositive(e.target.checked)} />
            <span className="switch-track"><span className="switch-thumb"></span></span>
          </label>
          <span>باف مثبت ({fa(pending.positive)} عدد){!pending.positive ? " — بافی کامل نشده" : ""}</span>
        </div>

        <p className="entity-modal-note">
          با تایید، {fa(selectedTotal)} باف انتخاب‌شده با تاریخ امروز به «تاریخچه باف» منتقل و از شمارش فعلی کسر می‌شود.
        </p>

        <label className="sr-only" htmlFor="applyBuffNote">توضیحات اعمال باف</label>
        <textarea id="applyBuffNote" className="textarea" placeholder="توضیحات دلیل اعمال این باف (اختیاری)…" value={note} onChange={(e) => setNote(e.target.value)} />
        <div className="modal-actions">
          <button className="btn" type="button" onClick={onCancel} disabled={busy}>انصراف</button>
          <button className="btn btn-primary" type="button" onClick={handleConfirm} disabled={busy || !selectedTotal}>{busy ? "در حال اعمال…" : "تایید و اعمال باف"}</button>
        </div>
      </section>
    </div>
  );
}

function SettingsModal({ open, settings, onCancel, onSave }) {
  const [neg, setNeg] = useState(settings.negativeThreshold);
  const [pos, setPos] = useState(settings.positiveThreshold);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setNeg(settings.negativeThreshold);
      setPos(settings.positiveThreshold);
      setError("");
      setBusy(false);
    }
  }, [open, settings]);

  if (!open) return null;

  async function handleSave() {
    const n = Number(neg);
    const p = Number(pos);
    if (!(n > 0) || !(p > 0)) {
      setError("آستانه‌ها باید عدد بزرگ‌تر از صفر باشند.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSave({ negativeThreshold: n, positiveThreshold: p });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "خطا در ذخیره تنظیمات");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-bg" style={{ display: "block" }} onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <section className="modal" role="dialog" aria-modal="true">
        <h3>تنظیمات آستانه باف</h3>
        <p className="entity-modal-note">این تنظیمات فقط در اختیار مدیر کل است و روی کل سیستم اعمال می‌شود.</p>

        <label className="settings-label" htmlFor="negThreshold">هر چند بازخورد منفی = ۱ باف منفی</label>
        <input id="negThreshold" className="input" type="number" min="1" value={neg} onChange={(e) => setNeg(e.target.value)} disabled={busy} />

        <label className="settings-label" htmlFor="posThreshold">هر چند بازخورد مثبت = ۱ باف مثبت</label>
        <input id="posThreshold" className="input" type="number" min="1" value={pos} onChange={(e) => setPos(e.target.value)} disabled={busy} />

        {error && <div className="login-error">{error}</div>}

        <div className="modal-actions">
          <button className="btn" type="button" onClick={onCancel} disabled={busy}>انصراف</button>
          <button className="btn btn-primary" type="button" onClick={handleSave} disabled={busy}>
            {busy ? "در حال ذخیره…" : "ذخیره تنظیمات"}
          </button>
        </div>
      </section>
    </div>
  );
}

function GroupAccessModal({ group, users, onToggle, onClose }) {
  if (!group) return null;
  const assignableUsers = users.filter(u => !u.permissions?.manageUsers);
  return (
    <div className="modal-bg" style={{ display: "block" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="modal" role="dialog" aria-modal="true">
        <h3>دسترسی کاربران به «{group.name}»</h3>
        <p className="entity-modal-note">کاربرانی که تیک بخورند، این گروه را در داشبورد خود می‌بینند. کاربران با دسترسی «مدیریت کاربران» همیشه به همهٔ گروه‌ها دسترسی دارند.</p>
        <div className="user-access-list">
          {assignableUsers.length === 0 && <div className="group-empty">کاربر دیگری برای تخصیص دسترسی ثبت نشده است.</div>}
          {assignableUsers.map(u => {
            const checked = u.accessGroups === "all" || (Array.isArray(u.accessGroups) && u.accessGroups.includes(group.id));
            return (
              <label key={u.id} className="user-access-row">
                <input type="checkbox" checked={checked} onChange={() => onToggle(u.id, group.id)} />
                <span>{u.name || u.username} <span className="meta">({u.username})</span></span>
              </label>
            );
          })}
        </div>
        <div className="modal-actions">
          <button className="btn btn-primary" type="button" onClick={onClose}>بستن</button>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSessionState] = useState(() => getSession());
  const [state, setState] = useState({
    groups: [],
    people: [],
    feedbacks: [],
    appliedBuffs: [],
    settings: { negativeThreshold: 5, positiveThreshold: 5 },
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(!!getSession());
  const [loadError, setLoadError] = useState("");
  const [reloadNonce, setReloadNonce] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [feedbackModalPersonId, setFeedbackModalPersonId] = useState(null);
  const [entityModal, setEntityModal] = useState({ mode: null, groupId: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accessModalGroupId, setAccessModalGroupId] = useState(null);
  const [applyBuffPersonId, setApplyBuffPersonId] = useState(null);
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  const historyPersonId = searchParams.get("history");
  const historyPage = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);

  function openHistory(personId) {
    const next = new URLSearchParams(searchParams);
    next.set("history", String(personId));
    next.set("page", "1");
    setSearchParams(next);
  }
  function closeHistory() {
    const next = new URLSearchParams(searchParams);
    next.delete("history");
    next.delete("page");
    setSearchParams(next);
  }
  function setHistoryPage(n) {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(n));
    setSearchParams(next);
  }

  function showToast(message) {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2200);
  }

  function persistSession(next) {
    setSession(next);
    setSessionState(next);
  }

  async function refreshState() {
    const next = await loadAppState();
    setState(next);
    if (!expandedGroupId && next.groups.length) {
      setExpandedGroupId(next.groups[0].id);
    }
    return next;
  }

  async function refreshUsers() {
    try {
      const res = await api.getUsers();
      setUsers((res.data?.users || []).map(mapApiUser));
    } catch (_) {
      setUsers([]);
    }
  }

  useEffect(() => {
    if (!session?.api_token) {
      setLoading(false);
      setLoadError("");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const me = await api.me();
        const user = me.data?.user || me.data;
        const nextSession = { api_token: session.api_token, user };
        if (cancelled) return;
        persistSession(nextSession);
        try {
          await refreshState();
          if (user?.permissions?.manageUsers) await refreshUsers();
        } catch (err) {
          if (!cancelled) {
            setLoadError(err instanceof ApiError ? err.message : "بارگذاری داده‌ها ناموفق بود.");
          }
        }
      } catch (err) {
        if (!cancelled) {
          if (err instanceof ApiError && err.code === "401") {
            clearSession();
            setSessionState(null);
          }
          setLoadError(err instanceof ApiError ? err.message : "نشست منقضی شده است.");
          if (!(err instanceof ApiError && err.code === "401")) {
            showToast(err instanceof ApiError ? err.message : "نشست منقضی شده است.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.api_token, reloadNonce]);

  useEffect(() => {
    function onUnauthorized() {
      clearSession();
      setSessionState(null);
      showToast("نشست منقضی شد. دوباره وارد شوید.");
    }
    window.addEventListener("buff:unauthorized", onUnauthorized);
    return () => window.removeEventListener("buff:unauthorized", onUnauthorized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const myUser = session?.user ? mapApiUser(session.user) : null;
  const canManageUsers = !!myUser?.permissions?.manageUsers;
  const canManageGroups = !!myUser?.permissions?.manageGroups;
  const canAddFeedback = !!myUser?.permissions?.addFeedback;
  const canAddPerson = !!myUser?.permissions?.addPerson;
  const canUpdate = !!myUser?.permissions?.update;
  const canDelete = !!myUser?.permissions?.delete;
  const canApplyBuff = !!myUser?.permissions?.applyBuff;
  const canManageSettings = !!myUser?.permissions?.settings;

  async function toggleUserGroupAccess(userId, groupId) {
    const user = users.find(u => String(u.id) === String(userId));
    if (!user || user.permissions?.manageUsers) return;
    let groupIds = Array.isArray(user.accessGroups) ? [...user.accessGroups] : (user.groups || []).map(g => g.id);
    groupIds = groupIds.map(Number);
    const gid = Number(groupId);
    groupIds = groupIds.includes(gid) ? groupIds.filter(id => id !== gid) : [...groupIds, gid];
    try {
      await api.updateUser(user.id, {
        name: user.name,
        username: user.username,
        permissions: user.permissions,
        group_ids: groupIds,
      });
      await refreshUsers();
      showToast("دسترسی گروه به‌روزرسانی شد.");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "خطا در به‌روزرسانی دسترسی");
    }
  }

  const feedbacksFor = (personId) =>
    state.feedbacks
      .filter(f => String(f.people_id) === String(personId))
      .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));

  const groupById = (id) => state.groups.find(g => String(g.id) === String(id)) || null;
  const personById = (id) => state.people.find(p => String(p.id) === String(id)) || null;
  const peopleForGroup = (groupId) => state.people.filter(p => String(p.group_id) === String(groupId));

  const searching = filters.personQuery.trim().length > 0;
  const membersFor = (groupId) => {
    const q = filters.personQuery.trim();
    return peopleForGroup(groupId).filter(p => !q || p.name.includes(q));
  };
  const accessibleGroups = state.groups.filter(g => userHasGroupAccess(myUser, g.id));
  const groupsToShow = accessibleGroups.filter(g =>
    filters.groupId === "all" || String(g.id) === String(filters.groupId)
  );

  const stats = useMemo(() => {
    let peopleCount = 0, feedbackCount = 0, negBuffTotal = 0, posBuffTotal = 0;
    groupsToShow.forEach(g => {
      membersFor(g.id).forEach(p => {
        peopleCount += 1;
        const list = feedbacksFor(p.id);
        feedbackCount += list.length;
        negBuffTotal += buffCount(list.filter(f => f.type === "negative" && !f.applied_buff_id).length, state.settings.negativeThreshold);
        posBuffTotal += buffCount(list.filter(f => f.type === "positive" && !f.applied_buff_id).length, state.settings.positiveThreshold);
      });
    });
    return { peopleCount, feedbackCount, negBuffTotal, posBuffTotal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, filters, myUser]);

  async function handleLogout() {
    try { await api.logout(); } catch (_) {}
    clearSession();
    setSessionState(null);
    setState({
      groups: [], people: [], feedbacks: [], appliedBuffs: [],
      settings: { negativeThreshold: 5, positiveThreshold: 5 },
    });
  }

  function toggleGroup(groupId) {
    setExpandedGroupId(prev => String(prev) === String(groupId) ? null : groupId);
  }

  async function addGroup(rawName) {
    if (!canManageGroups) return { error: "شما دسترسی ایجاد گروه را ندارید." };
    const name = rawName.trim();
    if (!name) return { error: "نام گروه را وارد کنید." };
    try {
      const res = await api.createGroup({ name });
      await refreshState();
      setExpandedGroupId(res.data?.group?.id);
      return { ok: true };
    } catch (err) {
      return { error: err instanceof ApiError ? err.message : "خطا در ایجاد گروه" };
    }
  }

  async function saveEntity(rawName) {
    const name = rawName.trim();
    if (!name) {
      showToast(entityModal.mode === "group" ? "نام گروه را وارد کنید." : "نام کارشناس را وارد کنید.");
      return;
    }
    if (entityModal.mode === "group") {
      const result = await addGroup(name);
      if (result.error) { showToast(result.error); return; }
      showToast("گروه جدید اضافه شد.");
    } else if (entityModal.mode === "person") {
      if (!canAddPerson) { showToast("شما دسترسی افزودن کارشناس را ندارید."); return; }
      try {
        await api.createPerson({ group_id: entityModal.groupId, name });
        await refreshState();
        setExpandedGroupId(entityModal.groupId);
        showToast("کارشناس به گروه اضافه شد.");
      } catch (err) {
        showToast(err instanceof ApiError ? err.message : "خطا در افزودن کارشناس");
        return;
      }
    }
    setEntityModal({ mode: null, groupId: null });
  }

  async function renameGroup(id) {
    if (!canUpdate) return;
    const group = groupById(id);
    if (!group) return;
    const next = window.prompt("نام جدید گروه:", group.name);
    if (next === null) return;
    const name = next.trim();
    if (!name || name === group.name) return;
    try {
      await api.updateGroup(id, { name });
      await refreshState();
      showToast("نام گروه تغییر کرد.");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "خطا در ویرایش گروه");
    }
  }

  async function deleteGroup(id) {
    if (!canDelete) return;
    const group = groupById(id);
    if (!group) return;
    if (peopleForGroup(id).length) {
      showToast("برای حذف گروه، ابتدا کارشناسان آن را حذف یا منتقل کنید.");
      return;
    }
    if (!window.confirm("گروه «" + group.name + "» حذف شود؟")) return;
    try {
      await api.deleteGroup(id);
      await refreshState();
      showToast("گروه حذف شد.");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "خطا در حذف گروه");
    }
  }

  async function addFeedbackRecord({ type, caption, imageFile }) {
    if (!feedbackModalPersonId || !canAddFeedback) return;
    try {
      await api.createFeedback({
        people_id: feedbackModalPersonId,
        type,
        caption,
        imageFile,
      });
      await refreshState();
      setFeedbackModalPersonId(null);
      showToast("بازخورد ثبت شد.");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "خطا در ثبت بازخورد");
    }
  }

  async function renamePerson(id) {
    if (!canUpdate) return;
    const p = personById(id);
    if (!p) return;
    const next = window.prompt("نام جدید:", p.name);
    if (next === null) return;
    const name = next.trim();
    if (!name) return;
    try {
      await api.updatePerson(id, { name });
      await refreshState();
      showToast("نام کارشناس تغییر کرد.");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "خطا در ویرایش کارشناس");
    }
  }

  async function deletePerson(id) {
    if (!canDelete) return;
    const p = personById(id);
    if (!p) return;
    if (!window.confirm("«" + p.name + "» و تمام تاریخچه بازخوردهایش حذف شود؟")) return;
    try {
      await api.deletePerson(id);
      await refreshState();
      showToast("کارشناس حذف شد.");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "خطا در حذف کارشناس");
    }
  }

  async function deleteFeedbackRecord(feedbackId, ownerPerson) {
    if (!canDelete) return;
    const label = ownerPerson ? " از تاریخچه «" + ownerPerson.name + "»" : "";
    if (!window.confirm("این رکورد بازخورد" + label + " حذف شود؟")) return;
    try {
      await api.deleteFeedback(feedbackId);
      await refreshState();
      showToast("رکورد بازخورد حذف شد.");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "خطا در حذف بازخورد");
    }
  }

  function pendingBuffGroupsFor(personId) {
    const result = {};
    ["negative", "positive"].forEach(type => {
      const threshold = type === "negative" ? state.settings.negativeThreshold : state.settings.positiveThreshold;
      const count = state.feedbacks.filter(f => String(f.people_id) === String(personId) && f.type === type && !f.applied_buff_id).length;
      result[type] = buffCount(count, threshold);
    });
    return result;
  }

  async function applyBuffsForPerson(personId, note, types) {
    if (!canApplyBuff) return;
    const typesToApply = ["negative", "positive"].filter(type => types ? types[type] : true);
    let total = 0;
    try {
      for (const type of typesToApply) {
        try {
          const res = await api.applyBuff({ people_id: personId, type, note: note || null });
          total += (res.data?.applied_buffs || []).length;
        } catch (err) {
          if (err instanceof ApiError && err.code === "100202") continue;
          throw err;
        }
      }
      await refreshState();
      setApplyBuffPersonId(null);
      if (!total) showToast("باف کامل‌شده‌ای برای اعمال وجود ندارد.");
      else showToast(total + " باف اعمال و به تاریخچه منتقل شد.");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "خطا در اعمال باف");
    }
  }

  async function saveSettings(next) {
    await api.updateSettings({
      negative_threshold: next.negativeThreshold,
      positive_threshold: next.positiveThreshold,
    });
    await refreshState();
    setSettingsOpen(false);
    showToast("تنظیمات ذخیره شد.");
  }

  function exportData() {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tracker_data.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    showToast("فایل داده‌ها آماده شد.");
  }

  if (!session) {
    return <Login onLogin={(nextSession) => {
      persistSession(nextSession);
      setLoading(true);
    }} />;
  }

  if (loading) {
    return <div className="login-screen"><div className="login-card"><p className="login-sub">در حال بارگذاری…</p></div></div>;
  }

  if (loadError && !state.groups.length) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <h1>خطا در بارگذاری</h1>
          <p className="login-sub">{loadError}</p>
          <button
            type="button"
            className="btn btn-primary login-submit"
            onClick={() => {
              setLoadError("");
              setReloadNonce((n) => n + 1);
            }}
          >
            تلاش مجدد
          </button>
          <button type="button" className="btn" style={{ marginTop: 8 }} onClick={handleLogout}>خروج</button>
        </div>
      </div>
    );
  }

  const feedbackModalPerson = personById(feedbackModalPersonId);
  const historyPerson = personById(historyPersonId);
  const accessModalGroup = accessModalGroupId ? groupById(accessModalGroupId) : null;
  const applyBuffPerson = personById(applyBuffPersonId);
  const applyBuffPending = applyBuffPersonId ? pendingBuffGroupsFor(applyBuffPersonId) : { negative: 0, positive: 0 };

  const personHandlers = {
    onAddFeedback: (id) => setFeedbackModalPersonId(id),
    onOpenHistory: (id) => openHistory(id),
    onRename: renamePerson,
    onDelete: deletePerson,
    onApplyBuff: (id) => {
      if (!canApplyBuff) return;
      const pending = pendingBuffGroupsFor(id);
      if (!pending.negative && !pending.positive) { showToast("باف کامل‌شده‌ای برای اعمال وجود ندارد."); return; }
      setApplyBuffPersonId(id);
    }
  };

  const canOpenAdmin = canManageUsers || canManageSettings || canManageGroups || canUpdate || canDelete;

  return (
    <div className="app-layout">
      <Sidebar
        userName={myUser?.name || session.username}
        canOpenAdmin={canOpenAdmin}
        onLogout={handleLogout}
      />
      <div className="app-main">
    <Routes>
      <Route path="/users" element={canManageUsers ? (
        <UsersPage
          groups={state.groups}
          currentUsername={session.user?.username || session.username}
          onBack={() => navigate("/admin")}
          onUsersChanged={refreshUsers}
        />
      ) : (
        <Navigate to="/" replace />
      )} />
      <Route path="/groups" element={canOpenAdmin ? (
        <GroupsPage
          groups={state.groups}
          people={state.people}
          canManageGroups={canManageGroups}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onBack={() => navigate("/admin")}
          onAddGroup={addGroup}
          onRenameGroup={renameGroup}
          onDeleteGroup={deleteGroup}
        />
      ) : (
        <Navigate to="/" replace />
      )} />
      <Route path="/history" element={
        <BuffHistoryPage
          state={state}
          currentUser={myUser}
          onBack={() => navigate("/")}
        />
      } />
      <Route path="/admin" element={canOpenAdmin ? (
        <AdminPage
          canManageSettings={canManageSettings}
          canManageUsers={canManageUsers}
          onOpenSettings={() => setSettingsOpen(true)}
          onExport={exportData}
          onOpenUsers={() => navigate("/users")}
          onOpenGroups={() => navigate("/groups")}
        />
      ) : (
        <Navigate to="/" replace />
      )} />
      <Route path="/" element={
        <main className="app-shell">
          <section className="hero" aria-labelledby="page-heading">
            <div className="hero-line">
              <div>
                <h1 id="page-heading">مدیریت بازخوردهای ثبت‌شده</h1>
                <p>کارشناسان را بر اساس واحد سازمانی مدیریت کنید؛ بازخوردهای مثبت و منفی، تاریخچه و باف هر فرد مستقل نگهداری می‌شود.</p>
              </div>
              <div className="hero-note">هر {fa(state.settings.negativeThreshold)} بازخورد منفی = ۱ باف منفی · هر {fa(state.settings.positiveThreshold)} بازخورد مثبت = ۱ باف مثبت</div>
            </div>
          </section>

          <section className="stats" aria-label="آمار">
            <article className="stat"><div className="stat-label">کارشناس‌ها</div><div className="stat-value">{fa(stats.peopleCount)}</div></article>
            <article className="stat"><div className="stat-label">کل بازخوردها</div><div className="stat-value">{fa(stats.feedbackCount)}</div></article>
            <article className="stat"><div className="stat-label">باف منفی</div><div className="stat-value">−{fa(stats.negBuffTotal)}</div></article>
            <article className="stat"><div className="stat-label">باف مثبت</div><div className="stat-value">+{fa(stats.posBuffTotal)}</div></article>
          </section>

          <FilterBar groups={accessibleGroups} filters={filters} onChange={setFilters} onReset={() => setFilters(EMPTY_FILTERS)} />

          <section className="toolbar" aria-label="مدیریت گروه‌ها">
            <div className="group-toolbar-copy">
              <div className="group-toolbar-title">گروه‌های کارشناسان</div>
              <div className="group-toolbar-sub">برای مشاهده کارشناسان روی نام هر گروه کلیک کنید.</div>
            </div>
            <div className="toolbar-actions">
              {canManageGroups && (
                <button className="btn btn-primary" type="button" onClick={() => setEntityModal({ mode: "group", groupId: null })}>
                  ＋ افزودن گروه
                </button>
              )}
              <button className="btn" type="button" onClick={async () => {
                try {
                  await refreshState();
                  showToast("به‌روزرسانی شد.");
                } catch (err) {
                  showToast(err instanceof ApiError ? err.message : "خطا در به‌روزرسانی");
                }
              }}>↻ به‌روزرسانی</button>
            </div>
          </section>

          <section className="groups" aria-live="polite">
            {groupsToShow.map(group => (
              <GroupCard
                key={group.id}
                group={group}
                members={membersFor(group.id)}
                feedbacksByPerson={feedbacksFor}
                settings={state.settings}
                canAddFeedback={canAddFeedback}
                canAddPerson={canAddPerson}
                canUpdate={canUpdate}
                canDelete={canDelete}
                canApplyBuff={canApplyBuff}
                canManageUsers={canManageUsers}
                isOpen={searching ? membersFor(group.id).length > 0 : String(expandedGroupId) === String(group.id)}
                onToggle={toggleGroup}
                onAddPerson={(groupId) => setEntityModal({ mode: "person", groupId })}
                onRenameGroup={renameGroup}
                onDeleteGroup={deleteGroup}
                onManageAccess={(groupId) => setAccessModalGroupId(groupId)}
                {...personHandlers}
              />
            ))}
          </section>

          {groupsToShow.length === 0 && (
            <div className="empty">
              {accessibleGroups.length === 0
                ? (canManageGroups
                  ? "هنوز گروهی ندارید. با «افزودن گروه» شروع کنید یا از پنل مدیریت اقدام کنید."
                  : "به هیچ گروهی دسترسی ندارید. از مدیر بخواهید دسترسی گروه را برای شما فعال کند.")
                : "با این فیلترها گروهی پیدا نشد."}
            </div>
          )}

          <FeedbackModal person={feedbackModalPerson} onCancel={() => setFeedbackModalPersonId(null)} onSave={addFeedbackRecord} />
          <EntityModal
            mode={entityModal.mode}
            onCancel={() => setEntityModal({ mode: null, groupId: null })}
            onSave={saveEntity}
          />
          <HistoryModal
            person={historyPerson}
            feedbacks={historyPerson ? feedbacksFor(historyPerson.id) : []}
            settings={state.settings}
            canDelete={canDelete}
            page={historyPage}
            onPageChange={setHistoryPage}
            onClose={closeHistory}
            onDeleteFeedback={deleteFeedbackRecord}
          />
          <GroupAccessModal
            group={accessModalGroup}
            users={users}
            onToggle={toggleUserGroupAccess}
            onClose={() => setAccessModalGroupId(null)}
          />
          <ApplyBuffModal
            person={applyBuffPerson}
            pending={applyBuffPending}
            onCancel={() => setApplyBuffPersonId(null)}
            onConfirm={(note, types) => applyBuffsForPerson(applyBuffPersonId, note, types)}
          />
        </main>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
        <SettingsModal
          open={settingsOpen && canManageSettings}
          settings={state.settings}
          onCancel={() => setSettingsOpen(false)}
          onSave={saveSettings}
        />
        <div className={"toast" + (toast ? " show" : "")} role="status">{toast}</div>
      </div>
    </div>
  );
}
